<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { IDEVIEWMODELS } from '@/utils/models'
import historyService from '@/services/ide/settings/history.service'
import projectsService from '@/services/ide/projects.service'
import { useIdeStore, type TExecutionHistory, type THtmlExecutionHistory } from '@/stores/ide.store'
import ideModal from '@/components/ide/ide/IdeModal.vue'
import { IDECONSTANT } from '@/utils/ide'
import BaseCheck from '@/components/forms/BaseCheck.vue'
import Button from '@/components/atoms/button/index.vue'

import moment from 'moment'
import settingService from '@/services/ide/settings/setting.service'

const ideStore = useIdeStore()
const route = useRoute()

const isMounted = ref<boolean>(false)
const loadProjectInProgress = ref<boolean>(false)

const filteredProjects = ref<any[]>([])
const selectedStartCreatedDate = ref<string | null>(null)
const selectedEndCreatedDate = ref<string | null>(null)

const isHtml = computed(() => {
  return ideStore.isHtml
})
const isBlockly = computed(() => {
  return ideStore.isBlockly
})
const isDisplayName = computed(() => {
  return ideStore.ideMeta?.langDisplayName
})
const title = computed(() => {
  return 'Recent ' + isDisplayName.value + ' Executions'
})

const mobileProjectTitle = computed(() => {
  return 'My ' + isDisplayName.value + ' Project Overview'
})

const isExecutionsMounted = computed(() => {
  return isMounted.value
})
const projects = computed(() => {
  return ideStore.isExecutionHistory || []
})
const isExecutions = computed(() => {
  if (projects.value === null) return false
  return projects.value.length > 0 || false
})
const project = computed(() => {
  return ideStore.isSelectedExecutionHistory
})
const isProject = computed(() => {
  return project.value !== null
})
const languageVersion = computed(() => {
  const codeProject = project.value as TExecutionHistory
  if (isProject.value && !isHtml.value)
    return ideStore.isVersions[codeProject.versionIndex ? codeProject.versionIndex : 0]
  else return 'Not Available'
})
/**
 * @description Load the execution history
 */
const loadHistory = () => {
  isMounted.value = false
  historyService.loadHistory()
  isMounted.value = true
}
/**
 * if the project is selected
 * @param project IProject
 * @returns boolean
 */
const isSelected = (project: TExecutionHistory | THtmlExecutionHistory) => {
  return ideStore.isSelectedExecutionHistory?.executedAt === project.executedAt || false
}
/**
 * @description Moment date
 * @param date string
 * @returns string
 */
const mmnt = (date: number) => {
  return moment(date).format('DD MMM YYYY, hh:mm:ss A')
}
/**
 * Load project
 * @param selecedProject IProject
 */
const loadProject = async (selecedProject: TExecutionHistory | THtmlExecutionHistory) => {
  loadProjectInProgress.value = true
  historyService.loadProject(selecedProject)
  loadProjectInProgress.value = false
}
/**
 * Open project
 * @param selecedProject IProject
 */
const openProject = async (
  selecedProject: TExecutionHistory | THtmlExecutionHistory | null = null
) => {
  if (selecedProject) {
    await loadProject(selecedProject)
  }
  projectsService.openProject(ideStore.isSelectedExecutionHistory)
  settingService.closePopup(IDEVIEWMODELS.EXECUTEHISTORY)
}
/**
 * Toggle recent storage
 */
const toggleRecentStorage = () => {
  if (ideStore.isLocalStoreEnabled) {
    localStorage.setItem(
      IDECONSTANT.STOTAGE_KEY,
      ideStore.isStorage.getItem(IDECONSTANT.STOTAGE_KEY)
    )
    sessionStorage.removeItem(IDECONSTANT.STOTAGE_KEY)
  } else {
    sessionStorage.setItem(
      IDECONSTANT.STOTAGE_KEY,
      ideStore.isStorage.getItem(IDECONSTANT.STOTAGE_KEY)
    )
    localStorage.removeItem(IDECONSTANT.STOTAGE_KEY)
  }
  historyService.checkStorage()
}
/**
 * clear all filters
 */
const clearFilters = () => {
  selectedStartCreatedDate.value = null
  selectedEndCreatedDate.value = null
  filteredProjects.value = projects.value
  isMounted.value = true
}
/**
 * filter projects
 */
const filterProjects = () => {
  filteredProjects.value = (projects.value as (TExecutionHistory | THtmlExecutionHistory)[]).filter(
    (project) => {
      let isMatched = true
      if (selectedStartCreatedDate.value && isMatched) {
        isMatched = new Date(project.executedAt) >= new Date(selectedStartCreatedDate.value)
      }
      if (selectedEndCreatedDate.value && isMatched) {
        isMatched = new Date(project.executedAt) <= new Date(selectedEndCreatedDate.value)
      }
      return isMatched
    }
  )
}
onMounted(async () => {
  await loadHistory()
  clearFilters()
  watch(projects, () => {
    clearFilters()
  })
  watch(route, () => {
    loadHistory()
    clearFilters()
  })
})
// Expose the clearFilters function
defineExpose({
  clearFilters
})
</script>
<template>
  <ideModal title="Recent Executions" class="my-auto">
    <div class="flex h-full gap-2 overflow-y-auto sm:h-[80vh] sm:w-[75vw] lg:max-w-[75vw]">
      <div v-if="isExecutionsMounted" class="w-full">
        <div
          class="border-primary flex w-full flex-col items-center gap-2 border-b pb-2.5 sm:gap-5 xl:flex-row"
        >
          <div
            class="flex w-full min-w-max flex-col justify-between gap-2 sm:flex-row sm:items-center md:max-w-lg"
          >
            <p class="w-24 min-w-max text-sm font-medium">Executed from:</p>
            <div div class="flex w-full flex-col justify-start gap-2 sm:w-full sm:flex-row">
              <input
                type="text"
                placeholder="Date from"
                v-model="selectedStartCreatedDate"
                onfocus="(this.type='date')"
                class="section-primary border-primary dark:focus-text-white text-md block w-full rounded-md border p-1 text-sm font-medium outline-none placeholder:text-gray-600 sm:w-fit sm:max-w-[134px] dark:[color-scheme:dark] dark:placeholder:text-gray-200"
              />

              <input
                type="text"
                placeholder="To"
                v-model="selectedEndCreatedDate"
                onfocus="(this.type='date')"
                class="section-primary border-primary dark:focus-text-white text-md block w-full rounded-md border p-1 text-sm font-medium outline-none placeholder:text-gray-600 sm:w-fit sm:max-w-[134px] dark:[color-scheme:dark] dark:placeholder:text-gray-200"
              />

              <!-- OLD INPUT (Date) -->

              <!-- <input
                v-model="selectedStartCreatedDate"
                type="date"
                placeholder="Date from"
                class="w-fit p-1 text-sm"
              /> -->

              <!-- <input
                v-model="selectedEndCreatedDate"
                type="date"
                placeholder="To"
                class="w-fit p-1 text-sm"
              /> -->

              <Button class="py-1.5 text-xs" @click="filterProjects()" variant="filled">
                Show results
              </Button>
            </div>
          </div>

          <!-- <div
            class="flex w-full items-center justify-between gap-2 rounded-lg sm:justify-start md:max-w-lg"
          >
            <label for="hs-basic-with-description-checked" class="my-2 block text-xs"
              >Remember execution history in this browser for future sessions?</label
            >
            <input
              type="checkbox"
              id="hs-basic-with-description-checked"
              class="checkbox-secondary"
              checked
              v-model="ideStore.isLocalStoreEnabled"
              @change="toggleRecentStorage"
            />
          </div> -->

          <BaseCheck
            :label="'Remember execution history in this browser for future sessions?'"
            v-model="ideStore.isLocalStoreEnabled"
            @change="toggleRecentStorage"
          />
        </div>
        <div
          class="flex w-full flex-col gap-5 pt-4 sm:h-[calc(100%_-130px)] sm:flex-row xl:h-[calc(100%_-_85px)]"
        >
          <!-- Recent Executions box -->
          <div
            :class="[
              'border-primary order-2  w-64  gap-2 pb-5 sm:order-1',
              {
                'w-full sm:w-[50%] sm:border-b-0 sm:border-r sm:pb-0 sm:pr-3 md:w-[50%]': !isBlockly
              },
              {
                'w-full': isBlockly
              }
            ]"
          >
            <p class="mb-2 hidden text-sm font-semibold sm:block">{{ title }}</p>
            <div v-show="isExecutions" class="h-[40vh] overflow-auto sm:h-full">
              <div
                v-for="project in filteredProjects"
                :key="project.executedAt"
                :class="[
                  'section-new-secondary mb-2.5 flex w-full cursor-pointer items-center justify-between rounded-lg border p-2',
                  {
                    'border-new-secondary-500 border-opacity-100': isSelected(project),
                    'stroke-primary-default border-opacity-50': !isSelected(project)
                  }
                ]"
                @click="loadProject(project)"
              >
                <p class="truncate text-sm font-medium">
                  {{ mmnt(project.executedAt) }}
                </p>
                <div class="flex gap-x-3">
                  <!-- <button v-show="!isBlockly" @click="loadProject(project)" class="cursor-pointer">
                    <FontAwesomeIcon icon="fa-eye"></FontAwesomeIcon>
                  </button> -->
                  <!-- <button @click="deleteProject(project)" class="cursor-pointer">
                    <FontAwesomeIcon icon="fa-trash"></FontAwesomeIcon>
                  </button> -->
                  <button @click.stop="openProject(project)" class="cursor-pointer">
                    <FontAwesomeIcon icon="fa-arrow-up-right-from-square"></FontAwesomeIcon>
                  </button>
                </div>
              </div>
            </div>
            <div v-show="!isExecutions">
              <span class="text-xs">You have no recent executions</span>
            </div>
          </div>
          <div
            v-show="!isBlockly"
            class="order-1 flex w-full flex-col gap-2 overflow-auto sm:order-2"
          >
            <h5 class="mb-2 hidden text-sm font-semibold sm:block">Project Overview</h5>
            <h5 class="mb-2 block text-sm font-semibold sm:hidden">{{ mobileProjectTitle }}</h5>
            <div
              v-show="!loadProjectInProgress && isProject"
              class="flex h-auto w-full flex-col justify-between gap-2 overflow-auto"
            >
              <div
                v-show="!isBlockly"
                :id="IDECONSTANT.EXECUTE_CODE_EDITOR"
                class="editor block h-48 w-full min-w-full"
              ></div>
              <div class="flex flex-col justify-between gap-2 pt-5">
                <p class="heading-xsmall text-left text-sm">Result</p>
                <div v-show="!isHtml">
                  <p class="p-xsmall mb-1">Language Version: {{ languageVersion }}</p>
                  <div
                    :id="IDECONSTANT.EXECUTE_OUTPUT_EDITOR"
                    :class="[
                      'editor h-24 w-full  bg-black text-white',
                      {
                        'h-64': isBlockly
                      }
                    ]"
                  ></div>
                </div>
                <div v-show="isHtml">
                  <div class="h-24 w-full bg-white">
                    <iframe
                      name="executionOutput"
                      frameborder="0"
                      height="100%"
                      width="100%"
                      sandbox=" allow-forms allow-popups allow-scripts allow-same-origin allow-modals"
                    ></iframe>
                  </div>
                  <div class="hidden">
                    <form
                      id="executionOutputForm"
                      name="html-download-form"
                      method="post"
                      action="/api/doodle/download/web.html"
                    >
                      <textarea id="executionOutputTextArea" name="code"></textarea>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="!isProject || loadProjectInProgress" class="text-sm">
              Select a project to preview
            </div>
          </div>
        </div>
      </div>

      <!-- <p class="p-xsmall max-w-sm md:max-w-xl lg:max-w-full">
        please note: this change is across all the languages, and this option will show this history
        to all the users using JDoodle in this browser.
      </p> -->
    </div>
  </ideModal>
</template>
